/**
 * DOM focus trap for dialog surfaces (Modal, Drawer). Built on the pure index
 * math in `focus-trap.ts`. On activation it moves focus into the container and
 * intercepts Tab / Shift+Tab to cycle within it; Escape invokes `onEscape`. The
 * returned disposer restores focus to the previously active element.
 */
import { nextIndex, prevIndex } from './focus-trap.ts';

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'textarea:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

/** All tabbable elements inside `container`, in document order. */
export function getFocusable(container: HTMLElement): HTMLElement[] {
  return Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
    (el) => el.offsetParent !== null || el === document.activeElement,
  );
}

/**
 * Activate a focus trap on `container`. Returns a disposer that detaches the
 * listener and restores focus.
 */
export function trapFocus(container: HTMLElement, onEscape: () => void): () => void {
  const previouslyFocused = document.activeElement as HTMLElement | null;
  let disposed = false;

  const focusFirst = (): void => {
    const focusable = getFocusable(container);
    (focusable[0] ?? container).focus();
  };
  // `container` is attached via a Solid `ref`, which — under a `<Portal>` —
  // fires while the subtree is still detached (Portal builds it via
  // `insert()` before `appendChild`-ing the mount container; see
  // solid-js/web's `Portal`). Focusing now would silently no-op. The
  // `appendChild` is synchronous JS in that same effect, so by the next
  // microtask the container is guaranteed connected.
  queueMicrotask(() => {
    if (!disposed) focusFirst();
  });

  const onKeyDown = (event: KeyboardEvent): void => {
    if (event.key === 'Escape') {
      event.preventDefault();
      onEscape();
      return;
    }
    if (event.key !== 'Tab') return;

    const focusable = getFocusable(container);
    if (focusable.length === 0) {
      event.preventDefault();
      return;
    }
    const current = focusable.indexOf(document.activeElement as HTMLElement);
    const target = event.shiftKey
      ? prevIndex(current, focusable.length)
      : nextIndex(current, focusable.length);
    focusable[target]?.focus();
    event.preventDefault();
  };

  container.addEventListener('keydown', onKeyDown);

  return () => {
    disposed = true;
    container.removeEventListener('keydown', onKeyDown);
    previouslyFocused?.focus?.();
  };
}
